import {IconDefinition} from '@fortawesome/fontawesome-svg-core';
import {faEllipsisVertical, faReply, faRotateRight, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {faSpinner} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex, Spin, Tooltip} from 'antd';
import {ReactNode, useEffect} from 'react';

import {Text} from '../../../../../component/text/text';
import {gray1} from '../../../../../css/var-export.scss';
import {Locale} from '../../../../../provider/locale/locale';
import {LangKeyType} from '../../../../../provider/locale/translation/type';
import {useSystem} from '../../../../../provider/system/system-hook';

import * as styles from './reviews-bulk-selection-bar.scss';

export type MenuItemConfigType = {
    langKey: LangKeyType;
    onClick: VoidFunction;
    icon?: IconDefinition;
    disabled?: boolean;
    disabledTooltipLangKey?: LangKeyType;
};

export type BulkSelectionBarProgressConfigType = {
    titleLangKey: LangKeyType;
    processedCount: number;
    totalCount: number;
    onCancel: VoidFunction;
};

export type BulkSelectionBarSummaryConfigType = {
    content: ReactNode;
    onRetry?: VoidFunction | null;
    onClose: VoidFunction;
};

type PropsType = {
    selectedCount: number;
    isVisible: boolean;

    toggleAllButtonConfig: {
        langKey: LangKeyType;
        onClick: VoidFunction;
    };

    onCancel: VoidFunction;

    submitButtonConfig: MenuItemConfigType & {disabledTooltipLangKey?: LangKeyType};
    menuItems?: Array<MenuItemConfigType>;
    isActionInProgress?: boolean;

    /** When set, the bar shows a running bulk job instead of the selection controls. */
    progressConfig?: BulkSelectionBarProgressConfigType | null;
    /** When set, the bar shows the result of the last bulk job until the user dismisses it. */
    summaryConfig?: BulkSelectionBarSummaryConfigType | null;
};

// eslint-disable-next-line complexity
export function ReviewsBulkSelectionBar(props: PropsType): JSX.Element | null {
    const {
        selectedCount,
        isVisible,
        toggleAllButtonConfig,
        onCancel,
        submitButtonConfig,
        menuItems,
        isActionInProgress,
        progressConfig,
        summaryConfig,
    } = props;

    const {screen} = useSystem();

    useEffect(() => {
        if ((screen.isDesktop || screen.isTablet || screen.isMobile) && isVisible) {
            document.body.dataset.tableControlPanelOpen = 'true';
        } else {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        }

        return () => {
            Reflect.deleteProperty(document.body.dataset, 'tableControlPanelOpen');
        };
    }, [isVisible, screen.isDesktop, screen.isTablet, screen.isMobile]);

    if (!isVisible) {
        return null;
    }

    if (progressConfig) {
        const handleProgressCancel = progressConfig.onCancel;

        return (
            <div className={styles.ReviewsBulkSelectionBar}>
                <Flex align="center" gap={12}>
                    <Spin indicator={<FontAwesomeIcon color={gray1} icon={faSpinner} spin />} size="small" />
                    <Text
                        className={styles.ReviewsBulkSelectionBar_selectedLabel}
                        stringKey={progressConfig.titleLangKey}
                    />
                    <span className={styles.ReviewsBulkSelectionBar_countBadge}>
                        <Locale
                            stringKey="REVIEWS__BULK_AI__PROGRESS_COUNTER"
                            valueMap={{
                                processedCount: String(progressConfig.processedCount),
                                totalCount: String(progressConfig.totalCount),
                            }}
                        />
                    </span>
                </Flex>
                <Flex align="center" gap={8}>
                    <Button
                        className={styles.ReviewsBulkSelectionBar_toggleAll}
                        onClick={handleProgressCancel}
                        size="small"
                        type="link"
                    >
                        <Locale stringKey="REVIEWS__BULK_AI__CANCEL_REMAINING" />
                    </Button>
                </Flex>
            </div>
        );
    }

    if (summaryConfig) {
        const handleSummaryRetry = summaryConfig.onRetry;
        const handleSummaryClose = summaryConfig.onClose;

        return (
            <div className={styles.ReviewsBulkSelectionBar}>
                <Flex align="center" gap={12}>
                    <span className={styles.ReviewsBulkSelectionBar_selectedLabel}>{summaryConfig.content}</span>
                </Flex>
                <Flex align="center" gap={8}>
                    {handleSummaryRetry && (
                        <Button
                            className={styles.ReviewsBulkSelectionBar_toggleAll}
                            icon={<FontAwesomeIcon icon={faRotateRight} />}
                            onClick={handleSummaryRetry}
                            size="small"
                            type="link"
                        >
                            <Locale stringKey="REVIEWS__BULK_AI__RETRY_FAILED" />
                        </Button>
                    )}
                    <Button
                        className={styles.ReviewsBulkSelectionBar_cancelButton}
                        icon={<FontAwesomeIcon icon={faXmark} />}
                        onClick={handleSummaryClose}
                        type="text"
                    />
                </Flex>
            </div>
        );
    }

    const isDisabled = selectedCount === 0;
    const isPanelBlocked = Boolean(isActionInProgress);

    function renderMenuLabel(langKey: LangKeyType, disabled: boolean, disabledTooltipLangKey?: LangKeyType) {
        const label = <Locale stringKey={langKey} />;

        if (disabled && disabledTooltipLangKey) {
            return (
                <Tooltip title={<Locale stringKey={disabledTooltipLangKey} />}>
                    <span className={styles.ReviewsBulkSelectionBar_menuItemTooltipLabel}>{label}</span>
                </Tooltip>
            );
        }

        return label;
    }

    const submitButtonDisabled = isDisabled || isPanelBlocked || (submitButtonConfig.disabled ?? false);
    const submitButtonDisabledTooltip: LangKeyType | null =
        submitButtonConfig.disabledTooltipLangKey && !isDisabled && submitButtonConfig.disabled
            ? submitButtonConfig.disabledTooltipLangKey
            : null;

    const submitButton = (
        <Button
            className={styles.ReviewsBulkSelectionBar_submitButton}
            disabled={submitButtonDisabled}
            onClick={() => submitButtonConfig.onClick()}
            type="primary"
        >
            <span className={styles.ReviewsBulkSelectionBar_submitLabel}>
                <Locale stringKey={submitButtonConfig.langKey} />
            </span>
            <span className={styles.ReviewsBulkSelectionBar_submitIcon}>
                <FontAwesomeIcon icon={faReply} />
            </span>
        </Button>
    );

    const dropdownItems = (menuItems ?? []).map((item, index) => {
        const disabled = (item.disabled ?? false) || isDisabled || isPanelBlocked;

        return {
            key: index,
            icon: item.icon ? <FontAwesomeIcon icon={item.icon} /> : null,
            label: renderMenuLabel(item.langKey, disabled, item.disabledTooltipLangKey),
            onClick: item.onClick,
            disabled,
        };
    });

    return (
        <div className={styles.ReviewsBulkSelectionBar}>
            <Flex align="center" gap={12}>
                <span className={styles.ReviewsBulkSelectionBar_countBadge}>
                    <Text bolder inheritColor>
                        {selectedCount}
                    </Text>
                </span>

                <Text className={styles.ReviewsBulkSelectionBar_selectedLabel} stringKey="REVIEWS__SELECTED_LABEL" />
                <Button
                    className={styles.ReviewsBulkSelectionBar_toggleAll}
                    disabled={isPanelBlocked}
                    onClick={() => toggleAllButtonConfig.onClick()}
                    size="small"
                    type="link"
                >
                    <Locale stringKey={toggleAllButtonConfig.langKey} />
                </Button>
            </Flex>
            <Flex align="center" gap={8}>
                {submitButtonDisabledTooltip ? (
                    <Tooltip title={<Locale stringKey={submitButtonDisabledTooltip} />}>
                        <span className={styles.ReviewsBulkSelectionBar_submitButtonTooltipWrap}>{submitButton}</span>
                    </Tooltip>
                ) : (
                    submitButton
                )}
                {dropdownItems.length > 0 && (
                    <Dropdown
                        disabled={isPanelBlocked}
                        getPopupContainer={(triggerNode) => triggerNode.parentElement ?? triggerNode}
                        menu={{items: dropdownItems}}
                        placement="topRight"
                        trigger={['click']}
                    >
                        <Button
                            className={styles.ReviewsBulkSelectionBar_menuButton}
                            disabled={isPanelBlocked}
                            icon={<FontAwesomeIcon icon={faEllipsisVertical} />}
                            loading={isPanelBlocked}
                            type="text"
                        />
                    </Dropdown>
                )}
                <Button
                    className={styles.ReviewsBulkSelectionBar_cancelButton}
                    disabled={isPanelBlocked}
                    icon={<FontAwesomeIcon icon={faXmark} />}
                    onClick={onCancel}
                    type="text"
                />
            </Flex>
        </div>
    );
}
